import { Box, Table, TableBody, TableHead } from '@mui/material'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useLicensingPaymentBreakdownCalculations } from '../../../../hooks/useLicensingPaymentBreakdownCalculations'
import { colors } from '../../../../stylesheet'
import { LicensingBill, LicensingPayment } from '../../../../swagger'
import { dateToSlashStringReinterpretedAsLocal } from '../../../../utils/dateUtility'
import {
  TableHeaderForPDF,
  TableHeaderForPDFProps,
} from '../../../Table/TableHeaderForPDF'
import {
  BreakdownLedgerForPDF,
  BreakDownLedgerPDFVariants,
} from '../BillingPDF/BreakdownLedgerForPDF'
import { LicensingFeeSubTotalBreakdown } from '../BillingPDF/LicensingFeeSubTotalBreakdown'
import { SemesterOneSubtotalBreakdown } from '../BillingPDF/SemesterOneSubtotalBreakdown'
import { SemesterTwoSubtotalBreakdown } from '../BillingPDF/SemesterTwoSubtotalBreakdown'
import { tutorLicensingFeeStyles } from './tutorLicensingFeeStyles'
import { convertTwoDecimalStringToBig } from '../../../../utils/bigJSHelper'

export interface LicensingBreakdownProps {
  licensingBill: LicensingBill
  licensingHistory: LicensingPayment[]
}

export const TutorLicensingBreakdown: React.FC<LicensingBreakdownProps> = ({
  licensingBill,
  licensingHistory,
}) => {
  const { t } = useTranslation()

  const programName = licensingBill._for.programType ?? ''
  const currencyCode = licensingBill.total.currencyCode
  const isChallenge = programName.includes('Challenge')
  const isLcd =
    licensingBill._for.programDirectorRole === 'Licensed Community Director'
  const semesterOneStartDate = licensingBill._for.semesterOneStartDate
  const paymentList = licensingHistory.filter(
    (payment) =>
      payment.licensingPayment.programKey === licensingBill._for.programKey
  )

  const tableHeaders: TableHeaderForPDFProps['tableHeaders'] = [
    {
      label: `${programName} - ${
        semesterOneStartDate
          ? dateToSlashStringReinterpretedAsLocal(semesterOneStartDate)
          : ''
      }`,
      align: 'left',
      cssProps: {
        ...tutorLicensingFeeStyles.header,
      },
    },
    {
      label: t('LicensingPayment.Breakdown.Header.FeeAmount', 'Fee Amount'),
      align: 'right',
      cssProps: {
        ...tutorLicensingFeeStyles.header,
      },
    },
    {
      label: t(
        'LicensingPayment.Breakdown.Header.NumberOfStudents',
        '# of Students'
      ),
      align: 'right',
      cssProps: {
        ...tutorLicensingFeeStyles.header,
      },
    },
    {
      label: t('LicensingPayment.Breakdown.Header.Subtotal', 'Subtotal'),
      align: 'right',
      cssProps: {
        ...tutorLicensingFeeStyles.header,
      },
      colSpan: 2,
    },
  ]

  const {
    balance,
    licensingFeeAdditionalStudent,
    licensingFeeFirstStudent,
    licensingFeeSubtotal,
    owed,
    paid,
    semester1AdditionalStudent,
    semester1FirstStudent,
    semester1Subtotal,
    semester2AdditionalStudent,
    semester2FirstStudent,
    semester2Subtotal,
  } = useLicensingPaymentBreakdownCalculations({ licensingBill })

  const licenseLabel = isLcd
    ? t('TutorLicensingBreakdown.LicenseLabel', 'License Fee')
    : t('TutorLicensingBreakdown.LicensingLabel', 'Licensing Tuition')

  const sem1SubHeaderLabel = isChallenge
    ? t(
        'TutorLicensingBreakdown.FirstSemesterSubHeader',
        '1st Semester {{licenseLabel}}',
        { licenseLabel }
      )
    : licenseLabel

  const sem2SubHeaderLabel = t(
    'TutorLicensingBreakdown.SecondSemesterSubHeader',
    '2nd Semester {{licenseLabel}}',
    { licenseLabel }
  )

  return (
    <Box
      style={{
        border: `1px solid ${colors.lightBlue}`,
        padding: 2,
      }}
    >
      <Table
        sx={{
          border: '1px solid white',
          '& tbody tr:nth-of-type(even)': { backgroundColor: '#f5f5f5' },
        }}
      >
        <TableHead>
          <TableHeaderForPDF tableHeaders={tableHeaders} />
        </TableHead>

        <TableBody>
          {convertTwoDecimalStringToBig(licensingFeeSubtotal).gt(0) && (
            <LicensingFeeSubTotalBreakdown
              licensingFeeSubtotal={licensingFeeSubtotal}
              licensingFeeFirstStudent={licensingFeeFirstStudent}
              licensingFeeAdditionalStudent={licensingFeeAdditionalStudent}
              subHeaderCellCssProps={{
                ...tutorLicensingFeeStyles.subHeader,
              }}
              subHeaderRowCssProps={{
                ...tutorLicensingFeeStyles.subHeaderRow,
              }}
              bodyCellCssProps={{ ...tutorLicensingFeeStyles.bodyCell }}
              subTotalCellCssProps={{
                borderTop: '.20em solid',
              }}
              nestedDescription
            />
          )}

          <SemesterOneSubtotalBreakdown
            semester1AdditionalStudent={semester1AdditionalStudent}
            semester1FirstStudent={semester1FirstStudent}
            semester1Subtotal={semester1Subtotal}
            isChallenge
            subHeaderCellCssProps={{
              ...tutorLicensingFeeStyles.subHeader,
            }}
            subHeaderRowCssProps={{
              ...tutorLicensingFeeStyles.subHeaderRow,
            }}
            bodyCellCssProps={{ ...tutorLicensingFeeStyles.bodyCell }}
            subTotalCellCssProps={{
              borderTop: '.20em solid',
            }}
            subHeaderLabel={sem1SubHeaderLabel}
            nestedDescription
          />
          <SemesterTwoSubtotalBreakdown
            semester2Subtotal={semester2Subtotal}
            semester2FirstStudent={semester2FirstStudent}
            semester2AdditionalStudent={semester2AdditionalStudent}
            isChallenge={isChallenge}
            subHeaderCellCssProps={{
              ...tutorLicensingFeeStyles.subHeader,
            }}
            subHeaderRowCssProps={{
              ...tutorLicensingFeeStyles.subHeaderRow,
            }}
            bodyCellCssProps={{ ...tutorLicensingFeeStyles.bodyCell }}
            subTotalCellCssProps={{
              borderTop: '.20em solid',
            }}
            subHeaderLabel={sem2SubHeaderLabel}
            nestedDescription
          />
          <BreakdownLedgerForPDF
            variant={BreakDownLedgerPDFVariants.Balance}
            totalOwed={owed}
            totalPaid={paid}
            balance={balance}
            licensingDue={licensingBill.licensingDue?.amount ?? '0.00'}
            currencyCode={currencyCode}
            programName={programName}
            paymentList={paymentList}
            subHeaderCellCssProps={{
              ...tutorLicensingFeeStyles.subHeader,
            }}
            subHeaderRowCssProps={{
              ...tutorLicensingFeeStyles.subHeaderRow,
            }}
            paymentHeaderCellCssProps={{
              ...tutorLicensingFeeStyles.paymentHeaderCell,
            }}
            bodyCellCssProps={{ ...tutorLicensingFeeStyles.bodyCell }}
            subTotalCellCssProps={{
              borderTop: '.20em solid',
            }}
            paymentBodyCellCssProps={{}}
          />
        </TableBody>
      </Table>
    </Box>
  )
}
